import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Boxes } from "lucide-react";
import { useLang } from "@/i18n";
import { PageHero, Reveal, StatusChip } from "@/components/common";
import { NftArt, NftCard } from "@/components/NftCard";
import { NFTS } from "@/data/nfts";

export default function NftDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const nft = NFTS.find((n) => n.slug === slug);

  if (!nft) {
    return (
      <div className="section-pad flex min-h-[60vh] flex-col items-center justify-center pt-32 text-center">
        <p className="font-display text-4xl uppercase text-paper">404</p>
        <Link to="/nfts" className="btn-gold mt-6" data-testid="back-to-gallery">{t("nft.back")}</Link>
      </div>
    );
  }

  const related = NFTS.filter((n) => n.slug !== slug && n.collection === nft.collection).slice(0, 3);

  return (
    <>
      <PageHero eyebrow={`${nft.id} · ${nft.collection}`} title={nft.name.toUpperCase()} sub={nft.desc[lang]} />

      <section className="section-pad grid gap-10 lg:grid-cols-[1fr_1fr]" data-testid="nft-detail">
        <Reveal>
          <div className="card-surface overflow-hidden">
            <div className="relative aspect-square">
              <NftArt nft={nft} />
              <div className="absolute left-4 top-4">
                <StatusChip status={nft.status} label={t("common.comingSoon")} />
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="space-y-5">
          <div className="card-surface p-6">
            <p className="eyebrow">{t("nft.attributes")}</p>
            <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {[
                [t("nft.identifier"), nft.id],
                [t("nft.collection"), nft.collection],
                [t("nft.rarity"), nft.rarity],
                [t("nft.status"), t("common.comingSoon")],
                [t("nft.owner"), t("nft.ownerNone")],
                [t("nft.onchain"), t("common.notDeployed")],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-mist">{k}</dt>
                  <dd className="mt-1 font-heading font-semibold capitalize text-paper">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="card-surface p-6">
            <p className="eyebrow">{t("nft.desc")}</p>
            <p className="mt-3 text-sm leading-relaxed text-mist">{nft.desc[lang]}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" disabled className="btn-neon cursor-not-allowed opacity-40" data-testid="mint-btn-disabled" title={t("mint.claimDisabled")}>
                {t("nft.mint")}
              </button>
              <Link to="/nfts" className="btn-ghost" data-testid="back-to-gallery">
                <ArrowLeft size={14} /> {t("nft.back")}
              </Link>
            </div>
            <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gold/80">
              <Boxes size={12} /> {t("mint.claimDisabled")}
            </p>
          </div>
        </Reveal>
      </section>

      {related.length > 0 && (
        <section className="section-pad mt-20">
          <h2 className="font-display text-3xl uppercase text-paper">{t("nft.collection")}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {related.map((n, i) => <NftCard key={n.slug} nft={n} index={i} />)}
          </div>
        </section>
      )}
    </>
  );
}
